<script setup lang="ts">
import { ref } from 'vue';

type inputType = 'text' | 'email' | 'number';

const emit = defineEmits<{
	(e: 'blur', value: string | number): void;
	(e: 'input', value: string | number): void;
	(e: 'change', value: string | number): void;
	(e: 'clearInput'): void;
}>();

const props = defineProps<{
	type?: inputType;
	value?: string | number;
	placeholder?: string;
	label?: string;
	disabled?: boolean;
	hasError?: boolean;
	errorLabel?: string;
	ariaLabel?: string;
	isSearch?: boolean;
	showClearBtn?: boolean;
	showCopyBtn?: boolean;
	isLoading?: boolean;
	altStyle?: boolean;
	showSpinner?: boolean;
	minValue?: number;
}>();

const inputEl = ref();

const handleClear = (): void => {
	inputEl.value.focus();
	emit('clearInput');
};

const handleCopyText = (): void => {
	if (props.value) navigator.clipboard.writeText(props.value.toString());
};
</script>

<template>
	<label class="input-field">
		<span v-if="label" class="input-field__label">
			<slot> {{ label }} </slot>
		</span>
		<span
			v-if="isSearch"
			class="input-field__search-icon bx bx-search text-md"
			data-ts="search-icon"
		/>
		<input
			ref="inputEl"
			:value="value"
			:aria-label="ariaLabel || 'input field'"
			:placeholder="placeholder"
			:class="[
				'input-field__input',
				{
					'input-field__input--error': hasError,
					'input-field__input--search': isSearch,
					'input-field__input--alt': altStyle,
					'input-field__input--no-spinner': !showSpinner,
				},
			]"
			:type="type || 'text'"
			:disabled="disabled"
			:min="minValue"
			data-ts="input-field"
			@blur="$emit('blur', $event.target.value)"
			@input="$emit('input', $event.target.value)"
			@change="$emit('change', $event.target.value)"
		/>
		<span v-if="isLoading" class="input-field__loading" data-ts="loading-label">loading</span>
		<span v-if="hasError" class="input-field__error" data-ts="error-label">
			{{ errorLabel }}
		</span>
		<button
			v-show="value && ((!disabled && showClearBtn) || showCopyBtn)"
			type="button"
			:title="showClearBtn ? 'Clear' : 'Copy'"
			:class="['input-field__action-btn bx text-md', showClearBtn ? ' bx-x' : 'bx-copy']"
			:aria-label="showClearBtn ? 'Clear input' : 'Copy text'"
			:data-ts="showClearBtn ? 'clear-input-btn' : 'copy-text-btn'"
			@click.prevent="showClearBtn ? handleClear() : handleCopyText()"
		/>
	</label>
</template>

<style lang="scss" scoped>
.input-field {
	position: relative;
	display: block;

	&__label {
		display: block;
		font-size: $font-size-sm;
		font-weight: $font-weight-medium;
		margin-bottom: $spacing-1;
	}

	&__input {
		width: 100%;
		height: $height-form-element;
		border-radius: $border-radius-2;
		border: $border-width-1 $border-style $color-gray-200;
		padding: $spacing-2 $spacing-2 $spacing-2 $spacing-2;
		text-overflow: ellipsis;
		font-size: $font-size;
		color: black;

		&--no-spinner::-webkit-outer-spin-button {
			display: none;
		}

		&--no-spinner::-webkit-inner-spin-button {
			display: none;
		}

		&--no-spinner {
			padding-right: $spacing-5;
		}

		&:focus-visible:not([disabled]) {
			outline: $border-width-2 $border-style $color-focus;
		}

		&:hover:not([disabled]) {
			border-color: $color-input-hover;
		}

		&--search {
			padding-left: $spacing-8;
		}

		&--error {
			border-color: $color-error-700;
		}

		&--alt {
			border-radius: 0;
			border: none;
			box-shadow: 0 1px 0 $color-gray-400;
			font-size: $font-size-lg;

			&:focus-visible:not([disabled]) {
				outline: none;
				box-shadow: 0 1px 0 $color-gray-700;
			}
		}

		&:disabled {
			color: black;
			border: $border-width-1 $border-style $color-gray-100;
			background-color: $color-gray-100 !important;
		}
	}

	&__error,
	&__loading {
		padding-left: $spacing-1;
		margin-top: $spacing-1;
		font-size: $font-size-sm;
	}

	&__error {
		color: $color-error-700;
	}

	&__loading {
		color: $color-pending-400;
	}

	&__action-btn,
	&__search-icon {
		position: absolute;
		height: $height-form-element;
		padding: 0 $spacing-2;
		font-size: $font-size-md;
		border-radius: $border-radius-2;
		border: $border-width-1 $border-style transparent;
	}

	&__action-btn {
		right: 0;
		transition: transform 300ms ease;

		&:focus-visible {
			border: $border-width-1 $border-style $color-gray-950;
		}

		&:active {
			transform: scale(0.8);
			transition: transform 300ms ease;
		}

		@media (hover: hover) {
			&:hover {
				color: $color-gray-950;
			}
		}
	}

	&__search-icon {
		display: flex;
		align-items: center;
		left: 0;
	}
}
</style>
